import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { UserService } from '../../../core/services/user.service';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'app-user-profile',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    template: `
    <div class="page-container">
      <div class="profile-card">
        <h2>My Profile</h2>
        
        <form [formGroup]="profileForm" (ngSubmit)="onSubmit()">
            
            <div class="form-grid">
                <div class="form-group">
                    <label>Email</label>
                    <input type="email" formControlName="email">
                </div>
                
                <div class="form-group">
                    <label>Phone</label>
                    <input type="text" formControlName="phone" placeholder="Phone">
                </div>

                <div class="form-group">
                    <label>First Name</label>
                    <input type="text" formControlName="firstName" placeholder="First Name">
                </div>

                <div class="form-group">
                    <label>Last Name</label>
                    <input type="text" formControlName="lastName" placeholder="Last Name">
                </div>

                <div class="form-group">
                    <label>Department</label>
                    <input type="text" formControlName="department" placeholder="Department">
                </div>

                <div class="form-group">
                    <label>Title</label>
                    <input type="text" formControlName="title" placeholder="Job Title">
                </div>
            </div>

            <button type="submit" [disabled]="profileForm.invalid || isLoading" class="btn-primary">
                {{ isLoading ? 'Saving...' : 'Update Profile' }}
            </button>
            
            <div *ngIf="message" [class.success]="isSuccess" class="alert">
                {{ message }}
            </div>
        </form>
      </div>
    </div>
  `,
    styles: [`
    @import '../../../../styles/variables';
    @import '../../../../styles/mixins';
    
    .page-container {
        @include flex-center;
        min-height: 80vh;
        padding: $spacing-md;
    }
    
    .profile-card {
        @include premium-card;
        background: #232323; // Specific request
        width: 100%;
        max-width: 800px;
        
        h2 {
            margin-bottom: $spacing-lg;
            text-align: center;
        }
    }

    .form-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: $spacing-md;
        margin-bottom: $spacing-lg;

        @media (min-width: 768px) {
            grid-template-columns: 1fr 1fr;
        }
    }
    
    .form-group {
        margin-bottom: 0;
        label { display: block; margin-bottom: 5px; color: $text-muted; }
        input, textarea { 
            @include premium-input; 
            width: 100%;
            &.readonly-input { opacity: 0.7; cursor: default; }
        }
        textarea {
            resize: vertical;
            font-family: monospace;
        }
    }
    
    .btn-primary { @include premium-button; width: 100%; }
    
    .alert {
        margin-top: $spacing-md;
        padding: $spacing-sm;
        border-radius: $radius-sm;
        background: rgba($error-color, 0.1);
        color: $error-color;
        text-align: center;
        &.success { background: rgba($success-color, 0.1); color: $success-color; }
    }
  `]
})
export class UserProfileComponent implements OnInit {
    private fb = inject(FormBuilder);
    private userService = inject(UserService);
    private cdr = inject(ChangeDetectorRef);

    profileForm = this.fb.group({

        email: ['', [Validators.required, Validators.email]],
        firstName: [''],
        lastName: [''],
        phone: [''],
        department: [''],
        title: ['']
    });

    isLoading = false;
    message = '';
    isSuccess = false;

    ngOnInit() {
        this.loadProfile();
    }

    loadProfile() {
        this.isLoading = true;
        this.userService.getProfile().subscribe({
            next: (data: any) => {
                this.profileForm.patchValue({

                    email: data.email,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phone: data.phone,
                    department: data.department,
                    title: data.title
                });
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Failed to load profile', err);
                this.isLoading = false;
                this.message = 'Failed to load profile data.';
            }
        });
    }

    onSubmit() {
        if (this.profileForm.valid) {
            this.isLoading = true;
            this.message = '';

            console.log('Submitting profile update:', this.profileForm.value);

            this.userService.updateProfile(this.profileForm.value)
                .pipe(finalize(() => {
                    this.isLoading = false;
                    this.cdr.detectChanges(); // Force UI update
                    console.log('Profile update request completed (success or error).');
                }))
                .subscribe({
                    next: (res) => {
                        console.log('Profile updated successfully:', res);
                        this.isLoading = false; // Double check
                        this.isSuccess = true;
                        this.message = 'Profile updated successfully';
                        this.cdr.detectChanges();
                    },
                    error: (err) => {
                        console.error('Failed to update profile:', err);
                        this.isLoading = false; // Double check
                        this.isSuccess = false;
                        this.message = 'Failed to update profile. Please try again.';
                        this.cdr.detectChanges();
                    }
                });
        }
    }
}
